import {useMutation} from '@apollo/client'
import {FormEvent, useEffect} from 'react'
import {Button, Form, Modal, Spinner} from 'react-bootstrap'
import {FormattedMessage, defineMessages, useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {ADD_MEMBER_ID_POLICY} from '../../../gql/mutations/policyMutation'
import {IResAddMemberIDPolicy} from '../soldTypes'

interface IModalProps {
  id: string
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  memberId: string
}

/** Llaves consumidas vía `t()` (el extractor no sigue wrappers, §17.12). */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const M = defineMessages({
  titleEdit: {id: 'policies.detail.modal.memberId.titleEdit', defaultMessage: 'Edit Member ID'},
  titleAdd: {id: 'policies.detail.modal.memberId.titleAdd', defaultMessage: 'Add Member ID'},
})
export const ModalMemberIdPolicy: React.FC<IModalProps> = ({
  id,
  isOpen,
  onClose,
  refetch,
  memberId,
}) => {
  const intl = useIntl()
  const t = (id: string, defaultMessage: string) => intl.formatMessage({id, defaultMessage})
  const errMsg = (message: string) =>
    intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message})
  const [gqlAdd, {data, loading, error}] = useMutation<IResAddMemberIDPolicy>(ADD_MEMBER_ID_POLICY)
  useEffect(() => {
    if (error) toast.error(errMsg(error.message))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  useEffect(() => {
    if (!data) return

    if (data && data.assignMemberIdToPolicy.success) {
      toast.success(
        intl.formatMessage({
          id: 'policies.detail.modal.memberId.toast.added',
          defaultMessage: 'Member ID added successfully',
        })
      )
      refetch()
      onClose()
    } else if (
      data.assignMemberIdToPolicy.errors &&
      data.assignMemberIdToPolicy.errors.length > 0
    ) {
      toast.error(errMsg(data.assignMemberIdToPolicy.errors[0].message))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const {memberId} = Object.fromEntries(new FormData(e.currentTarget))

    gqlAdd({
      variables: {
        id,
        memberId,
      },
    })
  }

  return (
    <Modal show={isOpen} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>
          {memberId
            ? t('policies.detail.modal.memberId.titleEdit', 'Edit Member ID')
            : t('policies.detail.modal.memberId.titleAdd', 'Add Member ID')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form id='formId' onSubmit={handleSubmit}>
          <Form.Group className='mb-3'>
            <Form.Label>
              <FormattedMessage
                id='policies.list.preview.field.memberId'
                defaultMessage='Member ID'
              />
            </Form.Label>
            <Form.Control
              defaultValue={memberId || ''}
              name='memberId'
              id='memberId'
              placeholder={t('policies.list.quickUpdate.phMemberId', 'Enter Member ID')}
              required
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onClose}>
          <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
        </Button>
        <Button
          variant='primary'
          type='submit'
          form='formId'
          formAction='formId'
          disabled={loading}
        >
          {loading && <Spinner animation='border' size='sm' className='me-2' />} <FormattedMessage id='common.action.saveChanges' defaultMessage='Save changes' />
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
